import { Section, Flow, Block } from "/components/Layout";
import PortableText from "/components/cms/portableText";
import Image from "/components/cms/image";
import Video from "components/Video";
import TwoRows from "components/TwoRows";
import FrontPageHero from "components/FrontPageHero";
import Box from "/components/Box";
import HeroWithTitle from "components/HeroWithTitle";
import Table from "../Table";
import CustomTwoColumns from "../StyledListWithCtaBox";
import BoxWithCta from "/components/BoxWithCta";
import WheeWave from "../WheeWave";
import ImageGallery from "../ImageGalery";
import TwoColumnsRectangleAndCircle from "../TwoColumnsRectangleAndCircle";
import ListWithNumbers from "../ListWithNumbers";
import SvgWave from "../SvgWave";
import CardGallery from "/components/CardGallery";
import ColumnsImages from "/components/ColumnsWithImages";
import BannerWithUps from "/components/BannerWithUps";
import OldBikeModelComponent from "/components/BikeModel";
import ArticleWithBackgroundEffect from "/components/ArticleWithBackgroundEffect";
import ListWithImages from "/components/ListWithImages";
import ThreeColumns from "/components/ThreeColumns";
import HeroFullScreenImage from "/components/HeroFullScreenImage";
import ScrollToTopButton from "/components/ScrollToTopButton";
import Faq from "/components/Faq";
import WhatIsIncluded from "/components/WhatIsIncluded";
import Locations from "/components/Locations";
import ArticleList from "/components/ArticleList";
import SpecsTable from "../cms/specsTable";
import Price from "../cms/price";
import BikeGridList from "../cms/bikeGridList";
import ChosenBikeList from "../cms/chosenBikes";
import { FrontPageChosenBike } from "/lib/sanity/queries/frontPage";

// Format content from Sanity CMS
const CmsBlock = ({ data }: any) => {
  const { _type: block = "" } = data;

  // Rich text
  if (block === "richText") {
    // Sanity wraps large content in a div, but not single content. Logic needed to target flow content.
    const isWrappedInDiv = data?.richText?.length > 1;
    const centerText = data?.centerText;

    if (!data?.richText) {
      return null;
    }

    return (
      <Section
        addBorder={data.addBorder}
        limitedWidth
        limitedWidthSmall={!data?.mediumWidth}
        center={centerText}
      >
        <Flow blockContent={isWrappedInDiv}>
          <PortableText blocks={data?.richText} />
        </Flow>
      </Section>
    );
  }

  // Image
  if (block === "image") {
    const width = {
      limitedWidth: true,
      limitedWidthSmall: false,
      limitedWidthXSmall: false
    };
    if (data?.size === "large") width.limitedWidth = true;
    if (data?.size === "medium") width.limitedWidthSmall = true;
    if (data?.size === "small") width.limitedWidthXSmall = true;
    return (
      <Section center outerNoLimit {...width}>
        <Image imageObject={data} />
      </Section>
    );
  }

  // Video
  if (block === "video") {
    return (
      <Section limitedWidth>
        <Video url={data.videoUrl} />
      </Section>
    );
  }

  // Two rows
  if (block === "twoRows") {
    return (
      <Section limitedWidth>
        <TwoRows data={data} />
      </Section>
    );
  }

  // Box
  if (block === "box") {
    const width = {
      limitedWidth: true,
      limitedWidthSmall: false,
      limitedWidthXSmall: false
    };
    if (data?.width === "large") width.limitedWidth = true;
    if (data?.width === "medium") width.limitedWidthSmall = true;
    if (data?.width === "small") width.limitedWidthXSmall = true;

    return (
      <Section color={data?.sectionBgColor} {...width} center={data?.center}>
        <Box data={data} />
      </Section>
    );
  }

  // FrontPageHero
  if (block === "frontpageHero") {
    return (
      <Section outerNoLimit outer="none" innerNoLimit center>
        <FrontPageHero data={data} />
      </Section>
    );
  }

  if (block === "heroFullScreenImage") {
    return (
      <Section outerNoLimit innerNoLimit outer="none">
        <HeroFullScreenImage data={data} />
      </Section>
    );
  }

  if (block === "articleWithBackgroundEffect") {
    return (
      <Section outerNoLimit limitedWidth center>
        <ArticleWithBackgroundEffect data={data} />
      </Section>
    );
  }

  if (block === "scrollToTopButton") {
    return (
      <Section limitedWidth center>
        <ScrollToTopButton data={data} />
      </Section>
    );
  }

  if (block === "locations") {
    return (
      <Section limitedWidth center>
        <Locations data={data} />
      </Section>
    );
  }

  if (block === "whatIsIncluded") {
    return (
      <Section limitedWidth center>
        <WhatIsIncluded data={data} />
      </Section>
    );
  }

  if (block === "listWithNumbers") {
    const showBackgroundEffect = data?.backgroundEffect;
    const backgroundColor = showBackgroundEffect ? "green-30" : "none";
    const outer = data?.verticalDirection ? "none" : undefined;

    return (
      <div>
        {showBackgroundEffect && <SvgWave />}
        <Section outer={outer} color={backgroundColor} innerNoLimit>
          <Section limitedWidth outer="none">
            <Block bottom={showBackgroundEffect ? 10 : 0}>
              <ListWithNumbers data={data} />
            </Block>
          </Section>
        </Section>
      </div>
    );
  }

  // FrontPageHero with title
  if (block === "heroWithTitle") {
    return (
      <Section outer="none" limitedWidth>
        <HeroWithTitle data={data} />
      </Section>
    );
  }

  // List
  if (block === "list") {
    return (
      <Section limitedWidth>
        <ListWithImages data={data} />
      </Section>
    );
  }

  // List with two columns
  if (block === "styledListWithCtaBox") {
    return (
      <Section center limitedWidth>
        <CustomTwoColumns data={data} />
      </Section>
    );
  }

  // table
  if (block === "table") {
    return (
      <Section limitedWidth>
        <Table data={data} />
      </Section>
    );
  }

  if (block === "specsTable") {
    return (
      <Section limitedWidth>
        <SpecsTable data={data} />
      </Section>
    );
  }

  // box with cta
  if (block === "boxWithCta") {
    return (
      <Section center limitedWidth>
        <BoxWithCta data={data} />
      </Section>
    );
  }

  // wheeWave
  if (block === "wheeWave") {
    return (
      <Section center limitedWidth>
        <WheeWave data={data} />
      </Section>
    );
  }

  // ImageGallery
  if (block === "gallery") {
    return (
      <Section color={data.bgColor}>
        <Section center>
          <ImageGallery data={data} />
        </Section>
      </Section>
    );
  }

  // box with cta
  if (block === "twoColumnsRectangleAndCircle") {
    return (
      <Section limitedWidth>
        <TwoColumnsRectangleAndCircle data={data} />
      </Section>
    );
  }

  // Divider
  if (block === "divider") {
    return (
      <Section outer="large" limitedWidth>
        <hr />
      </Section>
    );
  }

  // Bike for sale section
  if (block === "bikeModel") {
    const gridDisplay: boolean = data.gridDisplay ?? false;

    if (!gridDisplay) {
      return (
        <Section limitedWidth outer="small">
          {" "}
          <OldBikeModelComponent data={data} />{" "}
        </Section>
      );
    }

    return (
      <Section>
        <BikeGridList data={data} />
      </Section>
    );
  }

  // Card gallery
  if (block === "cardGallery") {
    return (
      <Section limitedWidth center>
        <CardGallery data={data} />
      </Section>
    );
  }

  // Grid columns with images
  if (block === "columnsWithImages") {
    const outer = data.withoutTopMargin ? "none" : "medium";
    return (
      <Section outer={outer} limitedWidth center>
        <ColumnsImages data={data} />
      </Section>
    );
  }

  // Grid columns with images
  if (block === "threeColumns") {
    return (
      <Section id={data.sectionId} limitedWidth center>
        <ThreeColumns data={data} />
      </Section>
    );
  }

  // Two columns with images
  if (block === "bannerWithUps") {
    return (
      <Section limitedWidth limitedWidthSmall={data?.smallWidth}>
        <BannerWithUps data={data} />
      </Section>
    );
  }

  // List of articles
  if (block === "articles") {
    return (
      <Section limitedWidth limitedWidthSmall={data?.smallWidth}>
        <ArticleList data={data} />
      </Section>
    );
  }

  // FAQ
  if (block === "faq") {
    return (
      <Section limitedWidthSmall>
        <Faq data={data} />
      </Section>
    );
  }

  if (block === "priceModule") {
    type PriceData = {
      subscriptionType: string;
      primary: boolean;
      showCta: boolean;
      ctaButtonText?: string;
    };
    const sanityData: {
      bikeModelId: string;
      priceModule: PriceData[];
    } = data;

    const priceData: PriceData[] = sanityData.priceModule;

    const elementData = priceData.map((data) => {
      // subscriptionType data is in format: 'subscriptionTypeId:"subscriptionName"'
      const name = data.subscriptionType.split(":")[1];
      const id = data.subscriptionType.split(":")[0];
      return {
        name: name,
        subscriptionProductId: id,
        showCta: data.showCta,
        primary: data.primary,
        ctaButtonText: data.ctaButtonText
      };
    });

    return (
      <Section>
        <Price bikeModelId={sanityData.bikeModelId} elementData={elementData}></Price>
      </Section>
    );
  }

  if (block === "chosenBikes") {
    const sanityData: { chosenBike: FrontPageChosenBike } = data;

    const chosenBikeIds = sanityData.chosenBike.map((val) => val.id);

    return (
      <Section>
        <ChosenBikeList chosenBikesSanityData={sanityData.chosenBike} bikeTypeIds={chosenBikeIds} />
      </Section>
    );
  }

  console.error("No presentation configured for block type:", block);
  return null;
};

export default CmsBlock;
